import { useEffect, useMemo, useState } from 'react';
import { useQuery, useSession } from 'common/hooks';
import { matchingEngineApi } from '../matching-engine.utils';
import type { TaggedEntity } from '../components/matching-engine-chat-interface.component';

const fetchSearchableEntities = async (term: string, ids: string[]): Promise<TaggedEntity[]> => {
  const response = await matchingEngineApi.get('matching-engine-search-entities', {
    params: {
      term: term || undefined,
      ids: ids.length ? ids.join(',') : undefined,
    },
  });
  return response.data?.data ?? response.data ?? [];
};

export const useSearchableEntities = (term = '', ids: string[] = []) => {
  const { account: user } = useSession();
  const [debouncedTerm, setDebouncedTerm] = useState(term.trim());
  const idsKey = ids.join(',');
  const stableIds = useMemo(() => idsKey.split(',').filter(Boolean), [idsKey]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedTerm(term.trim()), 250);
    return () => window.clearTimeout(timeout);
  }, [term]);

  return useQuery(
    ['matching-engine', user?.organizationId, 'search-entities', debouncedTerm, idsKey],
    () => fetchSearchableEntities(debouncedTerm, stableIds),
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 10 * 60 * 1000,
      enabled: !!user?.organizationId && (!!debouncedTerm || stableIds.length > 0),
      keepPreviousData: false,
    },
  );
};
